import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'

import reservationService from 'services/reservation/'
import { WEEKDAYS_WITH_START_ON_MONDAY } from 'settings/dayPicker'

interface UseGetReservationAvailabilitiesParams {
  patientUsername: string
  professionalUsername: string
}

interface Availability {
  startDate: Date
  endDate: Date 
}

export interface WeekDayOption {
  id: string | number
  label: string
  availabilities?: {
    id: number
    label: string
  }[]
}

interface UseGetReservationAvailabilitiesReturn {
  dayOptions: WeekDayOption[]
  isLoading: boolean
}

type WeekDays = {
  0: 'Segunda-feira',
  1: 'Terça-feira',
  2: 'Quarta-feira',
  3: 'Quinta-feira',
  4: 'Sexta-feira',
  5: 'Sábado',
  6: 'Domingo',
}

type Availabilities = {
  [weekDay in keyof WeekDays]: Availability[] 
}

const formatAvailabilities = (availabilities: Availability[]) => {
  return availabilities.map((availability, index) => ({
    id: index,
    label: format(availability.startDate, 'kk:mm'),
    startDate: availability.startDate,
    endDate: availability.endDate,
  }))
}

const getDayOptions = (availabilities: Availabilities): WeekDayOption[] => {
  return Object.entries(availabilities || {})
    .filter(([, av]) => av.length > 0)
    .map(([weekDay]) => ({
      id: Number(weekDay),
      label: WEEKDAYS_WITH_START_ON_MONDAY[Number(weekDay)] as string,
      availabilities: formatAvailabilities(availabilities[Number(weekDay)]),
    }))
}

export const useGetReservationAvailabilities = ({
  patientUsername, 
  professionalUsername 
}: UseGetReservationAvailabilitiesParams): UseGetReservationAvailabilitiesReturn => {
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['availabilities', patientUsername, professionalUsername],
    queryFn: () => reservationService.availabilities({
      patientUsername,
      professionalUsername
    }),
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  })
  
  const dayOptions = isSuccess ? getDayOptions(data?.availabilities) : []
 
  return {
    dayOptions,
    isLoading,
  }
}